import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { canSeeAny, SUGGESTION_TABS } from "../lib/amTabs";
import { scmAPI } from "../lib/api";
import {
    LayoutDashboard,
    Users,
    MessageCircle,
    Star,
    Package,
    Crown,
    Gem,
    AlertTriangle,
    ScanLine,
    UsersRound,
    ShoppingCart,
    ClipboardList,
    Newspaper,
    Settings,
    Sliders,
    ShieldCheck,
    FileText,
    Scale,
    GitCompare,
    LogOut,
    ChevronRight,
    Layers,
    FolderKanban,
    Receipt,
    ArrowDownUp,
    Link2,
    User,
    Sparkles,
    Menu,
    Mail as MailIcon,
    Radar,
    Heart,
    Snail,
} from "lucide-react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const Layout = ({ children }) => {
    const { user, logout, isSRM } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    // Auto-close the drawer when navigating on mobile
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // A category manager's project steps past their date: the red count on Projects (every 5 minutes).
    const [scmAlarms, setScmAlarms] = useState(0);
    useEffect(() => {
        if (user?.role !== "SCM" && user?.role !== "SRM") return;     // the SRM: his unassigned projects
        const load = () =>
            scmAPI
                .ownAlarms()
                .then((r) => setScmAlarms(r.data.overdue || 0))
                .catch(() => {});
        load();
        const t = setInterval(load, 300000);
        return () => clearInterval(t);
    }, [user?.role, location.pathname]);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const navItems = [
        {
            label: "Dashboard",
            icon: LayoutDashboard,
            path: "/dashboard/srm",
            roles: ["SRM"],
        },
        // Senior category manager: his department's pages; the SRM opens them under "Category managers".
        { label: "Dashboard", icon: LayoutDashboard, path: "/scm", roles: ["SCM"] },
        { label: "Stock alarm", icon: AlertTriangle, path: "/scm/stock", roles: ["SCM"] },
        { label: "Slow movers", icon: Snail, path: "/scm/slow", roles: ["SCM"] },
        { label: "Bundles", icon: Layers, path: "/scm/bundles", roles: ["SCM"] },
        { label: "Exclusives", icon: Crown, path: "/scm/exclusives", roles: ["SCM"] },
        { label: "Projects", icon: FolderKanban, path: "/scm/projects", roles: ["SCM"] },
        { label: "Price compare", icon: Scale, path: "/scm/prices", roles: ["SCM"] },
        { label: "Sunday brief", icon: Newspaper, path: "/srm-brief", roles: ["SRM"] },
        { label: "Mail", icon: MailIcon, path: "/mail", roles: ["SRM"] },
        { label: "Celebrities", icon: Star, path: "/celebrities", roles: ["SRM", "AM"], tabs: ["celebrities"] },
        { label: "Favorites", icon: Heart, path: "/favorites", roles: ["SRM", "AM"], tabs: ["favorites"] },
        { label: "Celebrity Coverage", icon: Radar, path: "/coverage", roles: ["SRM", "AM"], tabs: ["coverage"] },
        { label: "Sales", icon: Receipt, path: "/sales/tracking", roles: ["SRM"] },
        { label: "Boutique Sort", icon: ArrowDownUp, path: "/boutique-sort", roles: ["SRM", "AM"], tabs: ["sort"] },
        { label: "Sort performance", icon: ArrowDownUp, path: "/sort-performance", roles: ["AM"], tabs: ["sortperf"] },
        { label: "Scrape", icon: Link2, path: "/scrape", roles: ["SRM", "AM"], tabs: ["scrape"] },
        { label: "Bundles", icon: Layers, path: "/bundles", roles: ["SRM", "AM"], tabs: ["bundles"] },
        { label: "Projects", icon: FolderKanban, path: "/projects", roles: ["SRM", "AM"], tabs: ["projects"] },
        { label: "Stock alarm", icon: AlertTriangle, path: "/inventory/stock-alarm", roles: ["SRM", "AM"], tabs: ["stock"] },
        { label: "Photo → SKUs", icon: ScanLine, path: "/photo-skus", roles: ["SRM", "AM"], tabs: ["photos"] },
        { label: "Received Goods", icon: Package, path: "/inventory/received", roles: ["SRM"] },
        { label: "Exclusive Items", icon: Crown, path: "/inventory/exclusive", roles: ["SRM", "AM"], tabs: ["exclusives"] },
        { label: "Exclusive picks", icon: Gem, path: "/inventory/exclusive-picks", roles: ["SRM", "AM"], tabs: ["exclusives"] },
        { label: "What to add", icon: FileText, path: "/suggestions", roles: ["SRM", "AM"], tabs: SUGGESTION_TABS },
        { label: "Look-alike gaps", icon: UsersRound, path: "/lookalike-gaps", roles: ["SRM", "AM"], tabs: ["gaps"] },
        { label: "Purchasing briefs", icon: ShoppingCart, path: "/purchase-briefs", roles: ["SRM"] },
        { label: "Weekly scorecard", icon: ClipboardList, path: "/am-scorecard", roles: ["SRM", "AM"], tabs: ["scorecard"] },
        { label: "Compare prices", icon: Scale, path: "/compare-prices", roles: ["SRM"] },
        { label: "Category managers", icon: Package, path: "/scm", roles: ["SRM"] },
        { label: "Suggested Celebrities", icon: Sparkles, path: "/discovery", roles: ["SRM"] },
        { label: "Compare", icon: GitCompare, path: "/compare", roles: ["SRM"] },
        { label: "Reports", icon: FileText, path: "/reports", roles: ["SRM"] },
    ];

    const settingsItems = [
        { label: "Suggestion Settings", icon: Sliders, path: "/settings/suggestions", roles: ["SRM"] },
        { label: "Discovery Settings", icon: Sparkles, path: "/settings/discovery", roles: ["SRM"] },
        { label: "Admin Settings", icon: Settings, path: "/settings/admin", roles: ["SRM"] },
        { label: "WhatsApp", icon: MessageCircle, path: "/settings/whatsapp", roles: ["SRM"] },
        { label: "Manage Users", icon: Users, path: "/settings/users", roles: ["SRM"] },
        { label: "Security", icon: ShieldCheck, path: "/settings/security", roles: ["SRM"] },
    ];

    const filteredNav = navItems.filter((item) => item.roles.includes(user?.role) && (!item.tabs || canSeeAny(user, item.tabs)));
    const filteredSettings = settingsItems.filter((item) => item.roles.includes(user?.role));

    const SidebarBody = (
        <div className="flex h-full flex-col">
            {/* Logo */}
            <div className="flex h-16 items-center border-b border-border/40 px-6">
                <h1 className="font-heading text-xl font-bold tracking-tight">BOUTIQAAT</h1>
            </div>

            {/* Navigation */}
            <ScrollArea className="flex-1 px-3 py-4">
                <nav className="space-y-1">
                    {filteredNav.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === "/scm"}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                cn(
                                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                                    "hover:bg-white/5",
                                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
                                )
                            }
                        >
                            <item.icon className="h-4 w-4" strokeWidth={1.5} />
                            {item.label}
                            {(item.path === "/scm/projects" || (item.path === "/scm" && user?.role === "SRM")) && scmAlarms > 0 && (
                                <span className="ml-auto rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white" title="Project steps past their date">
                                    {scmAlarms}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {filteredSettings.length > 0 && (
                    <>
                        <Separator className="my-4 bg-border/40" />
                        <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                            Settings
                        </p>
                        <nav className="space-y-1">
                            {filteredSettings.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        cn(
                                            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                                            "hover:bg-white/5",
                                            isActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
                                        )
                                    }
                                >
                                    <item.icon className="h-4 w-4" strokeWidth={1.5} />
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </>
                )}
            </ScrollArea>

            {/* User section */}
            <div className="border-t border-border/40 p-4">
                <div className="mb-3 flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                        {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium">{user?.name}</p>
                        <p className="text-xs text-muted-foreground">{user?.role}</p>
                    </div>
                </div>
                <NavLink
                    to="/profile"
                    className={({ isActive }) =>
                        cn(
                            "flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm mb-2 transition-all",
                            "hover:bg-white/5",
                            isActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
                        )
                    }
                    data-testid="nav-profile"
                >
                    <User className="h-4 w-4" strokeWidth={1.5} />
                    My Profile
                </NavLink>
                <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-muted-foreground hover:bg-white/5 hover:text-white"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                >
                    <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    Sign Out
                </Button>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-screen bg-background">
            {/* Sidebar — desktop only, fixed */}
            <aside className="hidden md:block fixed left-0 top-0 z-40 h-screen w-64 border-r border-border/40 bg-card">
                {SidebarBody}
            </aside>

            {/* Sidebar — mobile drawer */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetContent side="left" className="p-0 w-72 bg-card border-border/40">
                    {SidebarBody}
                </SheetContent>
            </Sheet>

            {/* Main Content */}
            <main className="flex-1 md:ml-64 min-w-0">
                {/* Top bar with breadcrumb + mobile menu trigger */}
                <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border/40 bg-background/80 px-4 md:px-6 backdrop-blur-md">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="md:hidden -ml-2 text-foreground"
                                aria-label="Open menu"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                    </Sheet>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                        <span className="hidden sm:inline">Boutiqaat</span>
                        <ChevronRight className="hidden sm:inline h-3 w-3 flex-shrink-0" />
                        <span className="text-foreground truncate">
                            {[...navItems, ...settingsItems].find((i) => i.path === location.pathname)?.label ||
                                location.pathname.split("/").filter(Boolean).map((p) =>
                                    p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, " "),
                                ).join(" / ") || "Dashboard"}
                        </span>
                    </div>
                </header>

                {/* Page content */}
                <div className="p-4 md:p-6">{children}</div>
            </main>
        </div>
    );
};

export default Layout;
